import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { FileText, Rocket, CheckCircle2, XCircleIcon } from "lucide-react";
import { Link } from "@tanstack/react-router";
import { AuditPlanActions } from "./audit-plan-actions";
import { AuditPlanListItem } from "@/lib/audit-plan";

type AuditPlanListProps = {
  plans: AuditPlanListItem[];
};

type AuditPlanStatus = "Draft" | "Active" | "In Progress" | "Completed" | "Cancelled";

// Assignment interface to link to plans
export interface LinkedAssignment {
  id: string;
  name: string;
  auditPlanId: string;
  status: "Not Started" | "In Progress" | "Completed" | "Overdue" | "Blocked";
}

const statusConfig: Record<
  AuditPlanStatus,
  {
    icon: React.ElementType;
    color: string;
    badgeVariant: "default" | "secondary" | "destructive" | "outline";
  }
> = {
  Draft: { icon: FileText, color: "text-gray-500", badgeVariant: "outline" },
  Active: { icon: Rocket, color: "text-sky-600", badgeVariant: "default" },
  "In Progress": { icon: Rocket, color: "text-sky-600", badgeVariant: "default" },
  Completed: { icon: CheckCircle2, color: "text-green-600", badgeVariant: "default" },
  Cancelled: { icon: XCircleIcon, color: "text-red-600", badgeVariant: "destructive" },
};

export const AuditPlanList = ({ plans }: AuditPlanListProps) => {
  const onActionCompleted = () => {};

  return (
    <ScrollArea className="w-full whitespace-nowrap rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-[300px]">Plan Name</TableHead>
            <TableHead>Status</TableHead>
            <TableHead className="w-[150px]">Progress</TableHead>
            <TableHead>Linked Assignments</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {plans.length > 0 ? (
            plans.map((plan) => {
              const config = statusConfig["Draft"];
              const StatusIcon = config.icon;
              const statusColor = config.color;
              const badgeVariant = config.badgeVariant;

              let progressColor = "bg-sky-500";
              // if (plan.state === "Completed") progressColor = "bg-green-500";
              // else if (plan.status === "Draft") progressColor = "bg-gray-400";
              // else if (plan.status === "Cancelled") progressColor = "bg-red-500";
              // else if (plan.status === "In Progress") progressColor = "bg-sky-500";

              const linkedAssignments: LinkedAssignment[] = [];

              return (
                <TableRow key={plan.id}>
                  <TableCell>
                    <div className="font-medium">{plan.title}</div>
                    <div className="text-xs text-muted-foreground">
                      {plan.referenceYear}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant={badgeVariant}>
                      <StatusIcon className={`mr-1.5 h-3.5 w-3.5 ${statusColor}`} />
                      {plan.state}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Progress
                        value={plan.completionPercentage}
                        className="h-2 flex-grow"
                        indicatorClassName={progressColor}
                      />
                      <span className="text-xs text-muted-foreground">
                        {plan.completionPercentage}%
                      </span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {linkedAssignments.length > 0 ? (
                        linkedAssignments.map((assignment) => (
                          <Link
                            key={assignment.id}
                            to="/assignments/$assignmentId"
                            params={{ assignmentId: assignment.id }}
                          >
                            <Badge
                              variant="secondary"
                              className="cursor-pointer hover:bg-primary/20"
                            >
                              {assignment.name}
                            </Badge>
                          </Link>
                        ))
                      ) : (
                        <span className="text-xs text-muted-foreground">
                          No assignments linked
                        </span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    <AuditPlanActions
                      auditPlan={plan}
                      selectedActions={["show_detail", "defer", "delete"]}
                      onActionCompleted={onActionCompleted}
                    />
                  </TableCell>
                </TableRow>
              );
            })
          ) : (
            <TableRow>
              <TableCell colSpan={5} className="h-24 text-center">
                No audit plans found.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </ScrollArea>
  );
};
